import {CM_LOGGER_SCENE} from "@app/common/services/logging"
import {NgStyle} from "@angular/common"
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    HostListener,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
    Signal,
    viewChild,
} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {arrayDifferent, objectDifferent, objectFieldsDifferent} from "@app/template-editor/helpers/change-detection"
import {HELPER_OBJECTS_LAYER, OVERLAY_OBJECTS_LAYER} from "@app/template-editor/helpers/helper-objects"
import {ThreeMeshCurveControl} from "@app/template-editor/helpers/three-mesh-curve-control"
import {
    getNextThreeObjectPart,
    getThreeObjectPart,
    mathIsEqual,
    mathIsWithinEpsilon,
    ThreeObjectPart,
    threeObjectPartToSceneNodePart,
} from "@app/template-editor/helpers/three-object"
import {
    fromThreeMatrix,
    fromThreeVector,
    MAX_FAR_CLIP,
    MIN_NEAR_CLIP,
    SceneCameraParameters,
    threeCameraToSceneNode,
    setThreeCameraTransform,
    updateThreeCamera,
} from "@app/template-editor/helpers/three-utils"
import {ToneMappingRenderPass} from "@app/template-editor/helpers/tone-mapping-render-pass"
import {
    ObjectNode,
    MeshNode,
    getGroupPartNumber,
    INodeSceneInteractionsContext,
    isGroupPart,
    ObjectPathPart,
    TemplateNodePart,
    SceneNodes,
    ToneMapping,
    isSceneInteractiveNode,
    isMaterialLikeNode,
    isProceduralTemplateNode,
    MaterialAssignment,
    getRootTemplateNodeForObjectPath,
    getTemplateNodeForObjectPath,
    getNodeOwner,
    isNodeWithMatrixArrayLockedTransformParameter,
    calculateFov,
    cameraAimDirection,
    cameraLookAt,
    raycastCameraDistance,
    type SceneInteractiveNode,
    type TransformInteraction,
} from "@cm/template-nodes"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {MeshGeometryService} from "@app/template-editor/services/mesh-geometry.service"
import {
    getTemplateNodePartFromPosition,
    getVectorFromPosition,
    TemplateDropTarget,
    TemplateNodeDragService,
} from "@app/template-editor/services/template-node-drag.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {OrbitControls, outline, OutlineNode, Three as THREE, ThreeTsl as TSL, TransformControls} from "@cm/material-nodes/three"
import {Matrix4, Vector3} from "@cm/math"
import {colorToString, fetchOk} from "@cm/utils"
import {TypedImageData} from "@cm/utils/typed-image-data"
import {filter, Subject, Subscription, take} from "rxjs"
import {ThreeAnnotationRenderer} from "@app/template-editor/components/three-annotation-renderer/three-annotation-renderer"
import {captureSnapshot} from "@app/template-editor/helpers/snapshot"
import {OutputRenderPass} from "@app/template-editor/helpers/output-render-pass"
import {ThreeTextureTilesManagerService} from "@template-editor/services/three-texture-tiles-manager.service"
import {RenderMode, ThreeTiledTextureInstance, ThreeViewportTextureTileUpdater} from "@cm/three-texture-streaming-browser"
import {ALLOW_TILE_DEBUG_RENDER_MODES} from "@cm/three-texture-streaming"
import {Hotkeys} from "@common/services/hotkeys/hotkeys.service"
import {ScrollDispatcher} from "@angular/cdk/scrolling"
import {SceneViewRenderPass, ThreeImageData} from "@app/template-editor/helpers/scene-view-render-pass"

type Dimensions = {
    width: number
    height: number
}

function typedImageDataToThree(data: TypedImageData, premultipliedAlpha: boolean): ThreeImageData {
    if (data.colorSpace !== "linear") {
        throw new Error(`Unsupported color space: ${data.colorSpace}`)
    }

    return {
        buffer: (() => {
            const buffer = data.data.buffer as ArrayBuffer
            switch (data.dataType) {
                case "uint8":
                    return new Uint8Array(buffer, data.data.byteOffset, data.data.byteLength)
                case "uint16":
                    return new Uint16Array(buffer, data.data.byteOffset, data.data.byteLength / 2)
                case "float16":
                    return new Uint16Array(buffer, data.data.byteOffset, data.data.byteLength / 2)
                case "float32":
                    return new Float32Array(buffer, data.data.byteOffset, data.data.byteLength / 4)
                default:
                    throw new Error(`Unsupported data type: ${data.dataType}`)
            }
        })(),
        width: data.width,
        height: data.height,
        format: ((): THREE.PixelFormat => {
            switch (data.channelLayout) {
                case "RGBA":
                    return THREE.RGBAFormat
                case "L":
                    return THREE.RedFormat
                default:
                    throw new Error(`Unsupported channel layout: ${data.channelLayout}`)
            }
        })(),
        type: ((): THREE.TextureDataType => {
            switch (data.dataType) {
                case "uint8":
                    return THREE.UnsignedByteType
                case "uint16":
                    return THREE.UnsignedShortType
                case "float16":
                    return THREE.HalfFloatType
                case "float32":
                    return THREE.FloatType
                default:
                    throw new Error(`Unsupported data type: ${data.dataType}`)
            }
        })(),
        premultipliedAlpha,
    }
}

export type SceneCamera = {parameters: SceneNodes.Camera; transform?: Matrix4}

type ThreeObjectPartClickEventTarget = {
    threeObjectPart: ThreeObjectPart
    intersection: THREE.Intersection<THREE.Object3D>
}

export type TransformedNodePart = {
    templateNode: SceneInteractiveNode
    part: TemplateNodePart["part"]
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-three-template-scene-viewer",
    imports: [NgStyle],
    templateUrl: "./three-template-scene-viewer.component.html",
    styleUrl: "./three-template-scene-viewer.component.scss",
})
export class ThreeTemplateSceneViewerComponent implements OnInit, OnDestroy {
    private readonly logger = inject(CM_LOGGER_SCENE)

    readonly sceneManagerService = inject(SceneManagerService)
    private readonly meshGeometryService = inject(MeshGeometryService)
    private readonly threeSceneManagerService = inject(ThreeSceneManagerService)
    private readonly threeTextureTilesManagerService = inject(ThreeTextureTilesManagerService)
    readonly drag = inject(TemplateNodeDragService)

    protected readonly scrollDispatcher = inject(ScrollDispatcher)
    protected readonly elementRef = inject(ElementRef)

    private readonly $canvas = viewChild.required<ElementRef<HTMLDivElement>>("canvas")
    private readonly $controls = viewChild.required<ElementRef<HTMLDivElement>>("controls")
    private readonly $annotations = viewChild.required<ElementRef<HTMLDivElement>>("annotations")
    private readonly $canvasContainer = viewChild.required<ElementRef<HTMLDivElement>>("canvasContainer")
    private resizeObserver: ResizeObserver

    private readonly destroyRef = inject(DestroyRef)
    isDestroyed = false
    scrollableParent: HTMLElement | null = null

    private readonly defaultCameraSettings = {
        nearClip: MIN_NEAR_CLIP,
        farClip: MAX_FAR_CLIP,
        filmGauge: 36,
        focalLength: 50,
        shiftX: 0,
        shiftY: 0,
    }
    private threeCamera: THREE.PerspectiveCamera

    private threeViewportTextureTileUpdater: ThreeViewportTextureTileUpdater | undefined

    private renderPipeline: THREE.RenderPipeline | undefined
    private toneMappingRenderPass!: ToneMappingRenderPass
    private sceneViewRenderPass!: SceneViewRenderPass
    private outlinePass!: OutlineNode
    private secondaryOutlinePass!: OutlineNode
    private readonly outlineVisibility = TSL.uniform(1.0)
    private outputPass!: OutputRenderPass
    private annotationRenderer!: ThreeAnnotationRenderer

    private orbitControls!: OrbitControls
    private orbitControlsSubscription: Subscription = Subscription.EMPTY
    private readonly $orbitIsDragging = signal(false)
    private transformControls!: TransformControls
    private transformObject = new THREE.Group()
    private readonly $transformIsDragging = signal(false)
    private transformInteraction: TransformInteraction | undefined

    private raycaster = new THREE.Raycaster()

    private readonly $containerDimensions = signal<Dimensions>({width: 1, height: 1})
    readonly $renderDimensions = computed(() => {
        const selectedCameraNode = this.$camera()
        let {width, height} = this.$containerDimensions()
        const renderer = this.threeSceneManagerService.$renderer()
        const pixelRatio = renderer?.getPixelRatio() ?? 1

        if (
            selectedCameraNode &&
            (this.threeSceneManagerService.$displayMode() !== "configurator" || selectedCameraNode.parameters.realtimeSettings.adaptAspectRatio !== true)
        ) {
            const getCameraCutout = () => {
                const {resolutionX, resolutionY} = selectedCameraNode.parameters
                const aspectRatio = resolutionX / resolutionY

                const w1 = width
                const h1 = w1 / aspectRatio
                if (h1 <= height) return {x: 0, y: (height - h1) / 2, width: w1, height: h1}

                const h2 = height
                const w2 = h2 * aspectRatio
                if (w2 <= width) return {x: (width - w2) / 2, y: 0, width: w2, height: h2}

                throw new Error("Invalid aspect ratio")
            }

            const cutOut = getCameraCutout()
            width = cutOut.width
            height = cutOut.height
        }

        return {
            width: Math.floor(width),
            height: Math.floor(height),
            pixelRatio,
        }
    })

    readonly $transformedNodeParts = computed(() => {
        const selectedObjectPathParts = this.sceneManagerService.$selectedObjectPathParts()
        const selectedNodePartsWithDuplicates = selectedObjectPathParts.map<TemplateNodePart | false>((selectedObjectPathPart) => {
            const {path, part} = selectedObjectPathPart
            const templateNode = getRootTemplateNodeForObjectPath(path)
            if (isProceduralTemplateNode(templateNode) && path.length > 1) {
                const childNode = getTemplateNodeForObjectPath(path)
                const sceneNodes = templateNode.getSceneNodePartsForParameterNodePart({templateNode: childNode, part}, this.sceneManagerService)
                if (sceneNodes.length === 0) return false

                return {templateNode: childNode, part}
            } else {
                return {templateNode, part: path.length === 1 ? part : "root"}
            }
        })

        const selectedNodeParts = selectedNodePartsWithDuplicates.filter((part, index): part is TemplateNodePart => {
            if (!part) return false
            return (
                index ===
                selectedNodePartsWithDuplicates.findIndex((p) => {
                    if (!p) return false
                    return p.templateNode === part.templateNode && p.part === part.part
                })
            )
        })

        const selectedObjectParts = selectedNodeParts.filter(
            (selectedNodePart): selectedNodePart is TransformedNodePart =>
                isSceneInteractiveNode(selectedNodePart.templateNode) &&
                selectedNodePart.templateNode.canTransformPart(selectedNodePart.part, this.provideNodeSceneInteractionsContext()),
        )

        if (selectedObjectParts.length === 1) return selectedObjectParts
        return selectedObjectParts.filter(({templateNode}) => templateNode.canTransformWith(selectedNodeParts))
    })

    private renderingFinished = new Subject<void>()
    private resolutionChanged = new Subject<void>()

    readonly $camera = input.required<SceneCamera | undefined>({alias: "camera"})
    readonly $cameraMoveModeActive = input<boolean>(false, {alias: "cameraMoveModeActive"})
    readonly cameraViewChanged = output<{transform: Matrix4; target: Vector3}>()
    readonly $allowEdit = input<boolean>(true, {alias: "allowEdit"})
    readonly $allowSelect = input<boolean>(true, {alias: "allowSelect"})
    readonly $previewImageData = input<TypedImageData | null>(null, {alias: "previewImageData"})
    readonly $overlayPreviewImageData = input<boolean>(undefined, {alias: "overlayPreviewImageData"})

    private readonly $viewCameraMatrix = signal<Matrix4>(new Matrix4(), {equal: mathIsWithinEpsilon})
    private readonly $viewOrbitTarget = signal<Vector3>(new Vector3(), {equal: mathIsWithinEpsilon})

    private readonly $debugEnabled = signal(false)
    private readonly hotkeys = inject(Hotkeys)

    constructor() {
        this.hotkeys
            .addShortcut("control.shift.d")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.$debugEnabled.update((enabled) => !enabled)
                this.logger.debug("ThreeTemplateSceneViewerComponent debug mode:", this.$debugEnabled())
            })
        this.hotkeys
            .addShortcut("control.t")
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(() => this.$debugEnabled()),
            )
            .subscribe(async () => {
                this.logger.debug("ThreeTemplateSceneViewerComponent: Triggering update of legacy image data objects")
                await this.threeSceneManagerService.materialManagerService.triggerUpdateJobsForImageDataObjects({legacyOnly: true, skipFailed: false})
            })
        this.hotkeys
            .addShortcut("control.shift.t")
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(() => this.$debugEnabled()),
            )
            .subscribe(async () => {
                this.logger.debug("ThreeTemplateSceneViewerComponent: Triggering update of all image data objects")
                await this.threeSceneManagerService.materialManagerService.triggerUpdateJobsForImageDataObjects({legacyOnly: false, skipFailed: false})
            })
        if (ALLOW_TILE_DEBUG_RENDER_MODES) {
            this.hotkeys
                .addShortcut("1")
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(() => this.$debugEnabled()),
                )
                .subscribe(() => {
                    this.logger.debug("ThreeTemplateSceneViewerComponent: Setting tiled texture render mode to 'normal'")
                    ThreeTiledTextureInstance.renderMode = RenderMode.Normal
                    this.redraw()
                })
            this.hotkeys
                .addShortcut("2")
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(() => this.$debugEnabled()),
                )
                .subscribe(() => {
                    this.logger.debug("ThreeTemplateSceneViewerComponent: Setting tiled texture render mode to 'mip-level'")
                    ThreeTiledTextureInstance.renderMode = RenderMode.MipLevelVisualization
                    this.redraw()
                })
            this.hotkeys
                .addShortcut("3")
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(() => this.$debugEnabled()),
                )
                .subscribe(() => {
                    this.logger.debug("ThreeTemplateSceneViewerComponent: Setting tiled texture render mode to 'tile-index'")
                    ThreeTiledTextureInstance.renderMode = RenderMode.TileVisualization
                    this.redraw()
                })
            this.hotkeys
                .addShortcut("4")
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(() => this.$debugEnabled()),
                )
                .subscribe(() => {
                    this.logger.debug("ThreeTemplateSceneViewerComponent: Setting tiled texture render mode to 'tile-index'")
                    ThreeTiledTextureInstance.renderMode = RenderMode.TiledTextureIdVisualization
                    this.redraw()
                })
            this.hotkeys
                .addShortcut("5")
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(() => this.$debugEnabled()),
                )
                .subscribe(() => {
                    this.logger.debug("ThreeTemplateSceneViewerComponent: Setting tiled texture render mode to 'intra-tile-uv'")
                    ThreeTiledTextureInstance.renderMode = RenderMode.IntraTileUvVisualization
                    this.redraw()
                })
            this.hotkeys
                .addShortcut("6")
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(() => this.$debugEnabled()),
                )
                .subscribe(() => {
                    this.logger.debug("ThreeTemplateSceneViewerComponent: Setting tiled texture render mode to 'mip-shift'")
                    ThreeTiledTextureInstance.renderMode = RenderMode.MipShiftVisualization
                    this.redraw()
                })
            this.hotkeys
                .addShortcut("7")
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(() => this.$debugEnabled()),
                )
                .subscribe(() => {
                    this.logger.debug("ThreeTemplateSceneViewerComponent: Setting tiled texture render mode to 'uv'")
                    ThreeTiledTextureInstance.renderMode = RenderMode.UvVisualization
                    this.redraw()
                })
            this.hotkeys
                .addShortcut("8")
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(() => this.$debugEnabled()),
                )
                .subscribe(() => {
                    this.logger.debug("ThreeTemplateSceneViewerComponent: Setting tiled texture render mode to 'tile-index-f'")
                    ThreeTiledTextureInstance.renderMode = RenderMode.TileIndexFVisualization
                    this.redraw()
                })
            this.hotkeys
                .addShortcut("9")
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    filter(() => this.$debugEnabled()),
                )
                .subscribe(() => {
                    this.logger.debug("ThreeTemplateSceneViewerComponent: Setting tiled texture render mode to 'mip-level-frac'")
                    ThreeTiledTextureInstance.renderMode = RenderMode.MipLevelFracVisualization
                    this.redraw()
                })
        }
        this.hotkeys
            .addShortcut("+")
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(() => this.$debugEnabled()),
            )
            .subscribe(() => {
                this.logger.debug("ThreeTemplateSceneViewerComponent: Increasing min mip level for tiled textures")
                ThreeTiledTextureInstance.incMinMipLevel()
                this.redraw()
            })
        this.hotkeys
            .addShortcut("-")
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(() => this.$debugEnabled()),
            )
            .subscribe(() => {
                this.logger.debug("ThreeTemplateSceneViewerComponent: Decreasing min mip level for tiled textures")
                ThreeTiledTextureInstance.decMinMipLevel()
                this.redraw()
            })

        this.raycaster.params.Line.threshold = 0.4

        this.threeCamera = new THREE.PerspectiveCamera()
        this.threeCamera.position.set(-200, 150, 200)
        this.threeCamera.lookAt(0, 0, 0)
        this.threeCamera.updateMatrix()
        this.$viewCameraMatrix.set(fromThreeMatrix(this.threeCamera.matrix))

        /*Using an effect for this will cause flickering, because the effect is executed too late*/
        this.resolutionChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            const {width, height} = this.$renderDimensions()
            this.annotationRenderer.setSize(width, height)
            this.threeViewportTextureTileUpdater?.setSize(width, height)

            this.redrawImmediate()
        })

        effect(() => {
            const imageData = this.$previewImageData()

            const shouldRenderHelperObjects = this.$allowEdit() && !this.$cameraMoveModeActive()
            const overlayPreviewImageData = this.$overlayPreviewImageData() ?? true

            this.sceneViewRenderPass.setRenderHelperObjects(shouldRenderHelperObjects && overlayPreviewImageData)
            this.sceneViewRenderPass.setImageData(imageData && typedImageDataToThree(imageData, false))
            this.redraw()
        })

        const getOutlineThreeObjects = (outlineObjects: ThreeObjectPart[]): THREE.Object3D[] => {
            const uniqueObjects = new Set<THREE.Object3D>()
            outlineObjects.forEach((threeObjectPart) => {
                const {threeObject, part} = threeObjectPart
                const renderObject = threeObject.getRenderObject()

                const queryTreeObjectPart = (object: THREE.Object3D, found: boolean) => {
                    const threeObjectPart = getThreeObjectPart(object)
                    found = (found || (threeObjectPart && threeObjectPart.threeObject === threeObject && threeObjectPart.part === part)) ?? false
                    if (found && object.type === "Mesh") {
                        uniqueObjects.add(object)
                        return
                    }

                    object.children.forEach((child) => queryTreeObjectPart(child, found))
                }
                queryTreeObjectPart(renderObject, false)
            })

            return [...uniqueObjects]
        }

        let previousOutlineObjects: THREE.Object3D[] = []
        let previousSecondaryOutlineObjects: THREE.Object3D[] = []
        effect(() => {
            const bottomLevelObjects = getOutlineThreeObjects(
                this.sceneManagerService.$hoveredObjectPathParts().length !== 0
                    ? this.threeSceneManagerService.$hoveredObjects()
                    : this.threeSceneManagerService.$outlinedObjects(),
            )

            const topLevelOutlineObjects = getOutlineThreeObjects(
                this.sceneManagerService.$hoveredObjectPathParts().length !== 0
                    ? this.threeSceneManagerService.$hoveredTopLevelObjects()
                    : this.threeSceneManagerService.$outlinedTopLevelObjects(),
            )

            const outlineObjects = bottomLevelObjects.length === 0 ? topLevelOutlineObjects : bottomLevelObjects
            const secondaryOutlineObjects = bottomLevelObjects.length === 0 ? [] : topLevelOutlineObjects

            if (
                arrayDifferent(outlineObjects, previousOutlineObjects, (a, b) => a.uuid === b.uuid) ||
                arrayDifferent(secondaryOutlineObjects, previousSecondaryOutlineObjects, (a, b) => a.uuid === b.uuid)
            ) {
                this.outlinePass.selectedObjects = outlineObjects
                this.secondaryOutlinePass.selectedObjects = secondaryOutlineObjects
                this.redraw()
            }

            previousOutlineObjects = outlineObjects
            previousSecondaryOutlineObjects = secondaryOutlineObjects
        })

        effect(() => {
            const transformIsDragging = this.$transformIsDragging()
            if (!transformIsDragging) this.propagateTransformedNodeToObject()
        })

        effect(() => {
            const renderer = this.threeSceneManagerService.$renderer()
            if (!renderer) return

            this.renderPipeline?.dispose()
            this.renderPipeline = new THREE.RenderPipeline(renderer)
            this.renderPipeline.outputColorTransform = false
            this.renderPipeline.outputNode = this.outputPass
            this.resizeView()
        })

        effect(() => {
            this.threeSceneManagerService.$orbitIsDragging.set(this.$orbitIsDragging())
        })

        const isOrbitEnabled = () =>
            (this.$cameraMoveModeActive() && !this.$transformIsDragging()) ||
            (this.$camera()?.parameters?.targeted !== false &&
                ((this.threeSceneManagerService.$displayMode() === "configurator" && this.$camera()?.parameters.realtimeSettings.enableOrbiting !== false) ||
                    (this.$camera()?.transform === undefined && !this.$transformIsDragging())))
        effect(() => {
            this.orbitControls.enabled = isOrbitEnabled()
        })

        const previousCameraDifferent = (
            current: SceneCameraParameters | undefined,
            previous: SceneCameraParameters | undefined,
            fields: (keyof SceneCameraParameters)[],
        ) => {
            function isRealtimeSettingsData(obj: object): obj is SceneCameraParameters["realtimeSettings"] {
                return "enableOrbiting" in obj
            }

            if (current === undefined) return previous !== undefined
            else
                return objectFieldsDifferent(current, previous, fields, (valueA, valueB) => {
                    if (typeof valueA === "object" && typeof valueB === "object") {
                        if (!isRealtimeSettingsData(valueA) && !isRealtimeSettingsData(valueB)) return mathIsEqual(valueA, valueB)
                        else if (isRealtimeSettingsData(valueA) && isRealtimeSettingsData(valueB)) {
                            const keys = [
                                ...new Set([...Object.keys(valueA), ...Object.keys(valueB ?? {})]),
                            ] as (keyof SceneCameraParameters["realtimeSettings"])[]
                            return !previousCameraRealtimeSettingsDifferent(valueA, valueB, keys)
                        } else return false
                    }

                    return valueA === valueB
                })
        }

        const previousCameraRealtimeSettingsDifferent = (
            current: SceneCameraParameters["realtimeSettings"] | undefined,
            previous: SceneCameraParameters["realtimeSettings"] | undefined,
            fields: (keyof SceneCameraParameters["realtimeSettings"])[],
        ) => {
            function isToneMappingData(obj: object): obj is ToneMapping {
                return "mode" in obj
            }

            if (current === undefined) return previous !== undefined
            else
                return objectFieldsDifferent(current, previous, fields, (valueA, valueB) => {
                    if (typeof valueA === "object" && typeof valueB === "object") {
                        if (!isToneMappingData(valueA) && !isToneMappingData(valueB)) return mathIsEqual(valueA, valueB)
                        else if (isToneMappingData(valueA) && isToneMappingData(valueB)) return !objectDifferent(valueA, valueB, undefined)
                        else return false
                    }

                    return valueA === valueB
                })
        }
        const previousTransformDifferent = (current: Matrix4 | undefined, previous: Matrix4 | undefined) => {
            if (current === undefined) return previous !== undefined
            else if (previous === undefined) return true
            else return !mathIsEqual(current, previous)
        }

        let previousCamera: SceneCamera | undefined = undefined
        let previousCameraMoveModeActive = false
        effect(() => {
            const camera = this.$camera()
            const parameters = camera?.parameters
            const transform = camera?.transform
            const cameraMoveModeActive = this.$cameraMoveModeActive()

            let orbitControlNeedsUpdate = false

            const orbitEnabled = isOrbitEnabled()

            const previousCameraTransform = this.threeCamera.matrix
            const renderDimensions = this.$renderDimensions()

            let enableDamping = false
            let dampingFactor = 0.05
            if (camera) {
                if (this.threeSceneManagerService.$displayMode() === "editor" && previousCamera === undefined) this.orbitControls.saveState()
                const newTransform = previousTransformDifferent(transform, previousCamera?.transform) ? transform : undefined
                updateThreeCamera(
                    this.threeCamera,
                    {...camera.parameters, resolutionX: renderDimensions.width, resolutionY: renderDimensions.height},
                    newTransform,
                )

                enableDamping = camera.parameters.realtimeSettings.enableDamping && !this.$cameraMoveModeActive()
                dampingFactor = camera.parameters.realtimeSettings.dampingFactor ?? dampingFactor
            } else {
                updateThreeCamera(this.threeCamera, {...this.defaultCameraSettings, resolutionX: renderDimensions.width, resolutionY: renderDimensions.height})
                if (orbitEnabled && this.threeSceneManagerService.$displayMode() === "editor" && previousCamera !== undefined) this.orbitControls.reset()
            }

            if (this.orbitControls.enableDamping !== enableDamping) {
                this.orbitControls.enableDamping = enableDamping
                orbitControlNeedsUpdate = true
            }

            if (this.orbitControls.dampingFactor !== dampingFactor) {
                this.orbitControls.dampingFactor = dampingFactor
                orbitControlNeedsUpdate = true
            }

            if (!previousCameraTransform.equals(this.threeCamera.matrix)) orbitControlNeedsUpdate = true

            if (cameraMoveModeActive && camera?.transform) {
                const boundCameraChanged = camera.parameters.id !== previousCamera?.parameters.id
                if (!previousCameraMoveModeActive || boundCameraChanged) {
                    const pivot = this.computeOrbitPivot(camera.parameters, camera.transform, false)
                    this.orbitControls.target.set(pivot.x, pivot.y, pivot.z)
                    orbitControlNeedsUpdate = true
                }
            } else if (previousCameraDifferent(parameters, previousCamera?.parameters, ["target"])) {
                const target = parameters?.target
                if (target) {
                    this.orbitControls.target = new THREE.Vector3(target.x, target.y, target.z)
                    orbitControlNeedsUpdate = true
                }
            }

            if (
                cameraMoveModeActive !== previousCameraMoveModeActive ||
                previousCameraRealtimeSettingsDifferent(parameters?.realtimeSettings, previousCamera?.parameters.realtimeSettings, [
                    "enablePanning",
                    "screenSpacePanning",
                    "minPolarAngle",
                    "maxPolarAngle",
                    "minAzimuthAngle",
                    "maxAzimuthAngle",
                    "minDistance",
                    "maxDistance",
                ])
            ) {
                this.orbitControls.enablePan = cameraMoveModeActive || (parameters?.realtimeSettings.enablePanning ?? true)
                this.orbitControls.screenSpacePanning = cameraMoveModeActive || (parameters?.realtimeSettings.screenSpacePanning ?? true)
                this.orbitControls.minPolarAngle = (parameters?.realtimeSettings.minPolarAngle ?? 0) * (Math.PI / 180)
                this.orbitControls.maxPolarAngle = (parameters?.realtimeSettings.maxPolarAngle ?? 180) * (Math.PI / 180)
                this.orbitControls.minAzimuthAngle = (parameters?.realtimeSettings.minAzimuthAngle ?? -Infinity) * (Math.PI / 180)
                this.orbitControls.maxAzimuthAngle = (parameters?.realtimeSettings.maxAzimuthAngle ?? Infinity) * (Math.PI / 180)
                this.orbitControls.minDistance = parameters?.realtimeSettings.minDistance ?? 0
                this.orbitControls.maxDistance = parameters?.realtimeSettings.maxDistance ?? 10000
                orbitControlNeedsUpdate = true
            }

            if (orbitControlNeedsUpdate) {
                if (orbitEnabled) {
                    this.orbitControls.update()
                    //enables reset of the camera through configurator api
                    if (this.threeSceneManagerService.$displayMode() === "configurator") this.orbitControls.saveState()
                }
            }

            if (previousCameraRealtimeSettingsDifferent(parameters?.realtimeSettings, previousCamera?.parameters.realtimeSettings, ["toneMapping"])) {
                const toneMapping = parameters?.realtimeSettings.toneMapping
                this.toneMappingRenderPass.setToneMapping(toneMapping ?? {mode: "linear"})
                this.redraw()
            }

            if (previousCameraDifferent(parameters, previousCamera?.parameters, ["exposure"])) {
                const exposure = parameters?.exposure ?? 1
                this.toneMappingRenderPass.setExposure(exposure)
                this.redraw()
            }

            if (
                previousCameraDifferent(parameters, previousCamera?.parameters, ["focalDistance", "focalLength", "fStop"]) ||
                previousTransformDifferent(transform, previousCamera?.transform)
            ) {
                if (!parameters) this.sceneViewRenderPass.setDepthOfField(undefined)
                else {
                    const {focalDistance, focalLength, fStop} = parameters

                    if (!transform) this.sceneViewRenderPass.setDepthOfField(undefined)
                    else {
                        this.sceneViewRenderPass.setDepthOfField({
                            apertureSize: (focalLength * 0.1) / fStop,
                            focalDistance,
                        })
                    }
                }

                this.redraw()
            }

            this.updateViewCameraState()

            previousCamera = camera
            previousCameraMoveModeActive = cameraMoveModeActive
        })

        effect(() => {
            this.transformControls.setMode(this.sceneManagerService.$transformMode())
        })

        effect(() => {
            const {backgroundColor, backgroundImage} = this.getBackgroundSettings()

            const bgColorString = !backgroundColor ? "transparent" : colorToString(backgroundColor)
            const canvas = this.$canvas().nativeElement
            canvas.style.background = bgColorString

            canvas.style.backgroundImage = backgroundImage ? `url(${backgroundImage.url})` : "none"
            if (backgroundImage) {
                const {width: baseWidth, height: baseHeight} = this.$renderDimensions()
                const {zoom, shiftX, shiftY} = backgroundImage

                const zoomedWidth = baseWidth * zoom
                const zoomedHeight = baseHeight * zoom

                const offsetX = -(zoomedWidth - baseWidth) / 2 + -shiftX * baseWidth
                const offsetY = -(zoomedHeight - baseHeight) / 2 + shiftY * baseHeight

                canvas.style.backgroundSize = `${zoomedWidth}px ${zoomedHeight}px`
                canvas.style.backgroundPosition = `${offsetX}px ${offsetY}px`
                canvas.style.backgroundRepeat = "no-repeat"
            } else {
                canvas.style.backgroundSize = ""
                canvas.style.backgroundPosition = ""
                canvas.style.backgroundRepeat = ""
            }
        })

        effect(() => {
            const allowEdit = this.$allowEdit()

            this.threeSceneManagerService.modifyBaseScene((scene) => {
                if (allowEdit) {
                    scene.add(this.transformControls.getHelper())
                    scene.add(this.transformObject)
                } else {
                    scene.remove(this.transformControls.getHelper())
                    scene.remove(this.transformObject)
                }
            })
        })

        this.resizeObserver = new ResizeObserver(() => {
            this.resizeView()
        })

        this.scrollDispatcher
            .scrolled()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.scrolled()
            })
    }

    readonly $viewCamera: Signal<SceneNodes.Camera> = computed((): SceneNodes.Camera => {
        this.$viewCameraMatrix() // dependency
        return threeCameraToSceneNode(this.threeCamera, "viewCamera", [], this.$renderDimensions(), {
            ...this.$camera()?.parameters,
            target: this.$viewOrbitTarget(),
        })
    })

    private updateViewCameraState() {
        this.threeCamera.updateMatrix()
        this.$viewCameraMatrix.set(fromThreeMatrix(this.threeCamera.matrix))
        const {x, y, z} = this.orbitControls.target
        this.$viewOrbitTarget.set(new Vector3(x, y, z))
    }

    private propagateTransformedNodeToObject() {
        const transformedNodeParts = this.$transformedNodeParts()

        if (transformedNodeParts.length === 0) {
            if (this.transformControls.object) {
                this.transformControls.detach()
                this.redraw()
            }
        } else {
            const transformedNodePart = transformedNodeParts[0]

            const {templateNode, part} = transformedNodePart

            const transform = templateNode.getPartTransform(part, this.provideNodeSceneInteractionsContext())
            const newMatrix = new THREE.Matrix4().fromArray(transform.toArray())

            if (this.transformControls.object !== this.transformObject || !newMatrix.equals(this.transformObject.matrix)) {
                this.transformObject.matrix = newMatrix
                this.transformObject.matrix.decompose(this.transformObject.position, this.transformObject.quaternion, this.transformObject.scale)

                this.transformControls.attach(this.transformObject)
                this.redraw()
            }
        }
    }

    private transformControlsDraggingChanged = (
        event: {
            value: unknown
        } & THREE.Event<"dragging-changed", TransformControls>,
    ) => {
        const started: boolean = event.value as boolean
        this.$transformIsDragging.set(started)

        if (started) {
            this.sceneManagerService.beginModifyTemplateGraph()
            this.beginTransformInteractionSession()
        } else {
            const interaction = this.transformInteraction
            if (interaction) this.sceneManagerService.modifyTemplateGraph(() => interaction.commit())
            this.transformInteraction = undefined
            this.sceneManagerService.endModifyTemplateGraph()
        }

        this.transformControls.getHelper().visible = !started
        if (this.transformControls.getHelper().visible) this.propagateTransformedNodeToObject()
    }

    private beginTransformInteractionSession() {
        this.transformInteraction = undefined
        const transformedNodeParts = this.$transformedNodeParts()
        if (transformedNodeParts.length === 0) return

        const context = this.provideNodeSceneInteractionsContext()

        if (transformedNodeParts.length === 1) {
            const {templateNode, part} = transformedNodeParts[0]
            this.transformInteraction = templateNode.beginTransformInteraction?.(part, context)
            return
        }

        const members: {session: TransformInteraction; startWorld: Matrix4}[] = []
        for (const {templateNode, part} of transformedNodeParts) {
            if (!templateNode.canTransformWith(transformedNodeParts)) continue
            const session = templateNode.beginTransformInteraction?.(part, context)
            const startWorld = context.getTransformAccessor(templateNode)?.getTransform()
            if (session && startWorld) members.push({session, startWorld})
        }
        if (members.length === 0) return

        const proxyStartWorld = fromThreeMatrix(this.transformObject.matrix)
        this.transformInteraction = {
            update: (world) => {
                const delta = world.multiply(proxyStartWorld.inverse())
                for (const {session, startWorld} of members) session.update(delta.multiply(startWorld))
            },
            commit: () => members.forEach((m) => m.session.commit()),
            cancel: () => members.forEach((m) => m.session.cancel()),
        }
    }

    private transformControlsObjectChanged = () => {
        if (!this.transformInteraction) return
        this.transformInteraction.update(fromThreeMatrix(this.transformObject.matrix))
    }

    private scrolled = () => {
        this.redraw()
    }

    ngOnInit() {
        this.scrollableParent = this.scrollDispatcher.getAncestorScrollContainers(this.elementRef).at(0)?.getElementRef().nativeElement ?? null

        this.threeSceneManagerService.requestedRedraw$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({taaFrame, crossfadeAlpha}) => {
            this.render(taaFrame, crossfadeAlpha)
        })
        this.threeSceneManagerService.requestedResize$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.resizeView())

        void this.threeTextureTilesManagerService.createViewportTextureTileUpdater().then((updater) => {
            this.threeViewportTextureTileUpdater = updater
        })

        this.sceneViewRenderPass = new SceneViewRenderPass(this.threeSceneManagerService.getSceneReference(), this.threeCamera)

        this.toneMappingRenderPass = new ToneMappingRenderPass({mode: "linear"}, 1.0, this.sceneViewRenderPass)

        const applyOutlineEffect = (outlinePass: OutlineNode, input: THREE.Node<"vec4">, color: number, alpha: number) => {
            const {visibleEdge, hiddenEdge} = outlinePass

            const visibleEdgeColor = TSL.vec4(TSL.color(color), alpha)
            const hiddenEdgeColor = TSL.vec4(TSL.color(color), alpha)
            const edgeStrength = 4
            const coloredOutline = visibleEdge.mul(visibleEdgeColor).add(hiddenEdge.mul(hiddenEdgeColor)).mul(edgeStrength).mul(this.outlineVisibility)
            const clampedAlpha = coloredOutline.a.clamp(0.0, 1.0)
            const coloredOutlinePreMultiplied = TSL.vec4(coloredOutline.rgb.mul(clampedAlpha).clamp(0.0, 1.0), clampedAlpha)

            return coloredOutlinePreMultiplied.add(input.mul(TSL.sub(1.0, coloredOutlinePreMultiplied.a)))
        }

        this.outlinePass = outline(this.threeSceneManagerService.getSceneReference(), this.threeCamera, {
            edgeThickness: TSL.float(1),
            edgeGlow: TSL.float(0.25),
        })

        this.secondaryOutlinePass = outline(this.threeSceneManagerService.getSceneReference(), this.threeCamera, {
            edgeThickness: TSL.float(1),
            edgeGlow: TSL.float(0.25),
        })

        const output = applyOutlineEffect(
            this.outlinePass,
            applyOutlineEffect(this.secondaryOutlinePass, this.toneMappingRenderPass, 0x035c80, 1.0),
            0x05b8ff,
            1.0,
        )

        this.outputPass = new OutputRenderPass(output)

        const controls = this.$controls()
        this.orbitControls = new OrbitControls(this.threeCamera, controls.nativeElement)
        this.orbitControls.addEventListener("start", this.onOrbitStart)
        this.orbitControls.addEventListener("change", this.onOrbitChanged)
        this.orbitControls.addEventListener("end", this.onOrbitEnd)
        this.transformControls = new TransformControls(this.threeCamera, controls.nativeElement)
        this.transformControls.size = 2.0 / 5.0
        this.transformControls.addEventListener("dragging-changed", this.transformControlsDraggingChanged)
        this.transformControls.addEventListener("objectChange", this.transformControlsObjectChanged)
        this.transformControls.addEventListener("change", this.redraw)

        this.transformControls.getRaycaster().layers.disableAll()
        this.transformControls.getRaycaster().layers.enable(HELPER_OBJECTS_LAYER)
        this.transformControls.getRaycaster().layers.enable(OVERLAY_OBJECTS_LAYER)
        this.transformControls.getHelper().traverse((child) => child.layers.set(OVERLAY_OBJECTS_LAYER))
        this.raycaster.layers.enable(HELPER_OBJECTS_LAYER)
        this.raycaster.layers.enable(OVERLAY_OBJECTS_LAYER)

        this.annotationRenderer = new ThreeAnnotationRenderer({element: this.$annotations().nativeElement})

        this.resizeView()

        this.drag.draggedItem$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({dragSource, dropTarget}) => {
            const {component, position} = dropTarget
            if (component === this) {
                const draggedNode = this.drag.draggableSourceToTemplateNode(dragSource)
                if (!draggedNode) return

                const getTemplateNodePartFromPositionSafe = () => {
                    try {
                        return getTemplateNodePartFromPosition(position)
                    } catch {
                        return {templateNode: undefined, part: undefined}
                    }
                }

                const {templateNode, part} = getTemplateNodePartFromPositionSafe()

                if (templateNode && part && isSceneInteractiveNode(templateNode) && templateNode.isDropTargetFor(draggedNode, part)) {
                    this.sceneManagerService.hoverNodes(undefined)
                    templateNode.handleDroppedSource(draggedNode, part, this.provideNodeSceneInteractionsContext())
                } else if (isMaterialLikeNode(draggedNode)) {
                    this.sceneManagerService.hoverNodes(undefined)

                    if (templateNode && part && templateNode instanceof MeshNode) {
                        if (isGroupPart(part)) {
                            const slot = `${getGroupPartNumber(part)}`

                            const materialAssignment = templateNode.parameters.materialAssignments.parameters[slot]
                            if (materialAssignment)
                                this.sceneManagerService.modifyTemplateGraph(() =>
                                    templateNode.parameters.materialAssignments.updateParameters({
                                        [slot]: materialAssignment.clone({cloneSubNode: () => true, parameterOverrides: {node: draggedNode}}),
                                    }),
                                )
                            else
                                this.sceneManagerService.modifyTemplateGraph(() =>
                                    templateNode.parameters.materialAssignments.updateParameters({
                                        [slot]: new MaterialAssignment({parameters: {node: draggedNode, side: "front"}}),
                                    }),
                                )
                        }
                    }
                } else if (draggedNode instanceof ObjectNode && isNodeWithMatrixArrayLockedTransformParameter(draggedNode)) {
                    this.threeSceneManagerService.$cursorPosition.set(undefined)

                    const vector = getVectorFromPosition(position)

                    if (!getNodeOwner(draggedNode)) {
                        const transform = new Matrix4()
                        transform.setTranslation(vector)

                        const clonedNode = draggedNode.clone({
                            cloneSubNode: () => true,
                        })
                        //Do not use overrideParameters directly in clone above, because this will somehow kill the TS language service
                        clonedNode.updateParameters({lockedTransform: transform.toArray()})

                        this.sceneManagerService.modifyTemplateGraph((templateGraph) => templateGraph.parameters.nodes.addEntry(clonedNode))
                    } else {
                        const matrix = new Matrix4(draggedNode.parameters.lockedTransform)
                        const {quaternion, scale} = matrix.decompose()
                        const newMatrix = Matrix4.compose(vector, quaternion, scale)
                        this.sceneManagerService.modifyTemplateGraph(() => draggedNode.updateParameters({lockedTransform: newMatrix.toArray()}))
                    }
                }
            }
        })
    }

    ngAfterViewInit() {
        this.resizeObserver.observe(this.$canvasContainer().nativeElement)
    }

    ngOnDestroy() {
        this.threeSceneManagerService.modifyBaseScene((scene) => {
            scene.remove(this.transformControls.getHelper())
            scene.remove(this.transformObject)
        })
        this.transformControls.removeEventListener("dragging-changed", this.transformControlsDraggingChanged)
        this.transformControls.removeEventListener("objectChange", this.transformControlsObjectChanged)
        this.transformControls.removeEventListener("change", this.redraw)
        this.transformControls.dispose()
        this.orbitControls.removeEventListener("start", this.onOrbitStart)
        this.orbitControls.removeEventListener("change", this.onOrbitChanged)
        this.orbitControls.removeEventListener("end", this.onOrbitEnd)
        this.orbitControls.dispose()
        this.threeViewportTextureTileUpdater?.dispose()
        this.toneMappingRenderPass.dispose()
        this.outlinePass.dispose()
        this.secondaryOutlinePass.dispose()
        this.secondaryOutlinePass.dispose()
        this.outputPass.dispose()
        this.renderPipeline?.dispose()
        this.sceneViewRenderPass.dispose()

        this.redraw()

        this.isDestroyed = true
    }

    private renderInRect(callback: () => void) {
        if (this.isDestroyed) return

        const renderer = this.threeSceneManagerService.$renderer()
        if (!renderer) return

        const rendererRect = renderer.domElement.getBoundingClientRect()
        if (rendererRect.width <= 1 || rendererRect.height <= 1) return

        const scrollableParentRect = this.getScrollableParentRect()
        const clientRect = this.getCanvasRect()

        const x = clientRect.left - rendererRect.left
        const y = clientRect.top - rendererRect.top

        const drawRect = ((): DOMRect => {
            let left = x
            let top = y
            let right = x + clientRect.width
            let bottom = y + clientRect.height

            if (scrollableParentRect) {
                const outsideLeft = Math.max(0, scrollableParentRect.left - clientRect.left)
                const outsideTop = Math.max(0, scrollableParentRect.top - clientRect.top)
                const outsideRight = Math.max(0, clientRect.right - scrollableParentRect.right)
                const outsideBottom = Math.max(0, clientRect.bottom - scrollableParentRect.bottom)

                left += outsideLeft
                top += outsideTop
                right -= outsideRight
                bottom -= outsideBottom
            }

            left = Math.max(0, left)
            top = Math.max(0, top)
            right = Math.min(rendererRect.width, right)
            bottom = Math.min(rendererRect.height, bottom)

            return new DOMRect(left, top, right - left, bottom - top)
        })()

        const previousScissorTest = renderer.getScissorTest()
        const previousViewport = new THREE.Vector4()
        renderer.getViewport(previousViewport)
        const previousScissor = new THREE.Vector4()
        renderer.getScissor(previousScissor)

        if (this.threeSceneManagerService.isWebGPUMode()) {
            renderer.setScissorTest(true)
        }
        renderer.setViewport(x, y, clientRect.width, clientRect.height)
        renderer.setScissor(drawRect.x, drawRect.y, drawRect.width, drawRect.height)

        callback()

        renderer.setViewport(previousViewport.x, previousViewport.y, previousViewport.z, previousViewport.w)
        renderer.setScissor(previousScissor.x, previousScissor.y, previousScissor.z, previousScissor.w)
        renderer.setScissorTest(previousScissorTest)
    }

    private onOrbitStart = () => {
        this.$orbitIsDragging.set(true)
    }

    private onOrbitChanged = () => {
        if (this.orbitControls.enabled) {
            this.updateViewCameraState()

            this.redraw()

            if (this.orbitControlsSubscription.closed && (this.orbitControls.autoRotate || this.orbitControls.enableDamping)) {
                this.orbitControlsSubscription = this.renderingFinished.pipe(takeUntilDestroyed(this.destroyRef), take(1)).subscribe(() => {
                    if (this.orbitControlsSubscription) this.orbitControlsSubscription.unsubscribe()
                    this.orbitControls.update()
                })
            }
        }
    }

    private onOrbitEnd = () => {
        this.$orbitIsDragging.set(false)
        if (this.$cameraMoveModeActive() && this.orbitControls.enabled) {
            const camera = this.$camera()
            if (!camera) throw new Error("cameraMoveModeActive requires a bound camera")
            if (!camera.transform) throw new Error("cameraMoveModeActive requires a camera with a transform")
            this.cameraViewChanged.emit({
                transform: fromThreeMatrix(new THREE.Matrix4().compose(this.threeCamera.position, this.threeCamera.quaternion, this.threeCamera.scale)),
                target: fromThreeVector(this.orbitControls.target),
            })
        }
    }

    private render(taaFrame: number | null, crossfadeAlpha = 1.0) {
        const renderPipeline = this.renderPipeline
        if (!renderPipeline) return

        void this.threeViewportTextureTileUpdater?.update(this.threeSceneManagerService.getSceneReference(), this.threeCamera)
        if (crossfadeAlpha === 0) {
            this.sceneViewRenderPass.captureCrossfadeSource()
        }
        this.sceneViewRenderPass.setCrossfadeAlpha(crossfadeAlpha)
        this.sceneViewRenderPass.setTaaFrame(taaFrame)
        this.renderInRect(() => {
            this.threeSceneManagerService.onPreRender(this.threeCamera, taaFrame)
            renderPipeline.render()
        })
        this.annotationRenderer.render(this.threeSceneManagerService.getSceneReference(), this.threeCamera)
        this.renderingFinished.next()
    }

    private redraw = () => {
        this.threeSceneManagerService.redraw()
    }

    private redrawImmediate() {
        this.threeSceneManagerService.redraw({immediate: true})
    }

    private getCanvasRect() {
        return this.$canvas().nativeElement.getBoundingClientRect()
    }

    private getCanvasContainerRect() {
        return this.$canvasContainer().nativeElement.getBoundingClientRect()
    }

    private getScrollableParentRect() {
        return this.scrollableParent?.getBoundingClientRect()
    }

    getViewportSize() {
        const {width, height} = this.getCanvasContainerRect()
        return [width, height]
    }

    private resizeView() {
        const {width, height} = this.getCanvasContainerRect()
        this.$containerDimensions.set({width, height})
        this.resolutionChanged.next()
    }

    @HostListener("mousedown", ["$event"])
    onMouseDown(_downEvent: MouseEvent) {
        const {nativeElement} = this.$canvas()
        let isDragging = false

        const downX = _downEvent.clientX
        const downY = _downEvent.clientY

        const onMouseMove = (moveEvent: MouseEvent) => {
            /*When screensharing in teams (single window, not fullscreen), chromium emits mouse move events for some reason.
            This causes picking to fail. I.e. mouse down -> artificial move -> counts as drag -> no pick.*/
            if (moveEvent.clientX !== downX || moveEvent.clientY !== downY) isDragging = true
        }

        const onMouseUp = (upEvent: MouseEvent): void => {
            if (!isDragging) {
                const rect = nativeElement.getBoundingClientRect()
                this.onMouseClick(upEvent.clientX - rect.left, upEvent.clientY - rect.top, upEvent.shiftKey, upEvent.ctrlKey, upEvent.metaKey)
            }

            nativeElement.removeEventListener("mousemove", onMouseMove)
            nativeElement.removeEventListener("mouseup", onMouseUp)
        }

        nativeElement.addEventListener("mousemove", onMouseMove)
        nativeElement.addEventListener("mouseup", onMouseUp)
    }

    @HostListener("mousemove", ["$event"])
    onMouseMove(moveEvent: MouseEvent) {
        if (this.sceneManagerService.$watchingForClickedSceneNodePart()) {
            const {nativeElement} = this.$canvas()
            const rect = nativeElement.getBoundingClientRect()
            const objectPathPartsUnderCursor = this.getObjectPathPartsUnderCursor(moveEvent.clientX - rect.left, moveEvent.clientY - rect.top, false)
            if (objectPathPartsUnderCursor.length > 0) this.sceneManagerService.hoverNodes([objectPathPartsUnderCursor[0].objectPathPart])
            else this.sceneManagerService.hoverNodes(undefined)
        }
    }

    protected onMouseClick(pointX: number, pointY: number, shiftKey: boolean, ctrlKey: boolean, metaKey: boolean) {
        if (!this.$allowEdit() && !this.$allowSelect() && !this.sceneManagerService.$watchingForClickedSceneNodePart()) return

        if (this.sceneManagerService.$watchingForClickedSceneNodePart()) this.sceneManagerService.hoverNodes(undefined)

        const sceneNodePartsUnderCursor = this.getSceneNodePartsUnderCursor(pointX, pointY, true)

        this.sceneManagerService.handleClickEvent({
            target: sceneNodePartsUnderCursor,
            modifiers: {shiftKey, ctrlKey, metaKey},
        })
    }

    protected getObjectsUnderCursor(pointX: number, pointY: number, meshGroups: boolean): ThreeObjectPartClickEventTarget[] {
        const {width, height} = this.getCanvasRect()
        const x = (pointX / width) * 2 - 1
        const y = -((pointY / height) * 2 - 1)

        this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.threeCamera)

        const intersectedObjects: ThreeObjectPartClickEventTarget[] = []
        this.raycaster
            .intersectObjects(this.threeSceneManagerService.getManagedThreeReferences(), true)
            .filter(
                (intersection) => intersection.object.visible && intersection.distance > this.threeCamera.near && intersection.distance < this.threeCamera.far,
            )
            .forEach((intersection) => {
                const {object} = intersection
                const threeObjectPart = getNextThreeObjectPart(object)
                if (threeObjectPart) {
                    const mappedThreeObjectPart = (threeObjectPart: ThreeObjectPart): ThreeObjectPart => {
                        if (!meshGroups) {
                            const {part} = threeObjectPart
                            if (part.match(/group\d+/)) return {threeObject: threeObjectPart.threeObject, part: "root"}
                        }

                        return threeObjectPart
                    }

                    intersectedObjects.push({threeObjectPart: mappedThreeObjectPart(threeObjectPart), intersection})
                }
            })

        return intersectedObjects
    }

    protected getSceneNodePartsUnderCursor(pointX: number, pointY: number, meshGroups: boolean) {
        const objectsUnderCursor = this.getObjectsUnderCursor(pointX, pointY, meshGroups).filter(
            ({threeObjectPart}) => !(threeObjectPart.threeObject.getSceneNode().selectable === false),
        )

        const isPriorizedPart = (threeObjectPart: ThreeObjectPart) =>
            threeObjectPart.part === "target" || threeObjectPart.threeObject instanceof ThreeMeshCurveControl

        const targetPriorizedObjectsUnderCursor = [
            ...objectsUnderCursor.filter(({threeObjectPart}) => isPriorizedPart(threeObjectPart)),
            ...objectsUnderCursor.filter(({threeObjectPart}) => !isPriorizedPart(threeObjectPart)),
        ]

        return targetPriorizedObjectsUnderCursor.map(({threeObjectPart, intersection}) => {
            const sceneNodePart = threeObjectPartToSceneNodePart(threeObjectPart)
            return {sceneNodePart, intersection}
        })
    }

    protected getObjectPathPartsUnderCursor(pointX: number, pointY: number, meshGroups: boolean) {
        const sceneNodePartsUnderCursor = this.getSceneNodePartsUnderCursor(pointX, pointY, meshGroups)

        const result: {objectPathPart: ObjectPathPart; intersection: THREE.Intersection}[] = []
        for (const {sceneNodePart, intersection} of sceneNodePartsUnderCursor) {
            result.push({
                objectPathPart: {
                    path: sceneNodePart.sceneNode.path,
                    part: sceneNodePart.part,
                },
                intersection,
            })
        }

        return result
    }

    dragOver(event: DragEvent) {
        const dragSource = this.drag.$dragSource()
        if (!dragSource) return

        const draggedNode = this.drag.draggableSourceToTemplateNode(dragSource)
        if (!draggedNode) return

        if (isMaterialLikeNode(draggedNode)) {
            this.drag.$dropTarget.update((previous) => {
                const {nativeElement} = this.$canvas()
                const rect = nativeElement.getBoundingClientRect()
                const objectPathPartsUnderCursor = this.getObjectPathPartsUnderCursor(event.clientX - rect.left, event.clientY - rect.top, true)

                if (objectPathPartsUnderCursor.length === 0) {
                    this.sceneManagerService.hoverNodes(undefined)
                    return null
                }

                const {objectPathPart} = objectPathPartsUnderCursor[0]
                const {path, part} = objectPathPart

                if (path.length === 0) {
                    this.sceneManagerService.hoverNodes(undefined)
                    return null
                }

                const templateNode = getRootTemplateNodeForObjectPath(path)

                if (!((isSceneInteractiveNode(templateNode) && templateNode.isDropTargetFor(draggedNode, part)) || templateNode instanceof MeshNode)) {
                    this.sceneManagerService.hoverNodes(undefined)
                    return null
                }

                this.sceneManagerService.hoverNodes([{path: [templateNode], part: objectPathPart.part}])

                const materialAssignment = (() => {
                    if (isSceneInteractiveNode(templateNode))
                        return templateNode.getPartMaterialAssignment ? templateNode.getPartMaterialAssignment(part) : null

                    if (!isGroupPart(part)) return null
                    const slot = `${getGroupPartNumber(part)}`
                    return templateNode.parameters.materialAssignments.parameters[slot]
                })()

                if (materialAssignment && materialAssignment.parameters.node === draggedNode) return null

                const dropTarget = {
                    component: this,
                    position: {templateNode, part: objectPathPart.part},
                } as TemplateDropTarget<ThreeTemplateSceneViewerComponent>
                const templateNodePartEqual = (a: TemplateNodePart, b: TemplateNodePart) => a.part === b.part && a.templateNode === b.templateNode

                if (
                    previous &&
                    previous.component === dropTarget.component &&
                    templateNodePartEqual(getTemplateNodePartFromPosition(previous.position), getTemplateNodePartFromPosition(dropTarget.position))
                )
                    return previous

                return dropTarget
            })
        } else if (draggedNode instanceof ObjectNode) {
            this.drag.$dropTarget.update((previous) => {
                const {nativeElement} = this.$canvas()
                const rect = nativeElement.getBoundingClientRect()
                const objectPathPartsUnderCursor = this.getObjectPathPartsUnderCursor(event.clientX - rect.left, event.clientY - rect.top, false)

                if (objectPathPartsUnderCursor.length === 0) {
                    this.threeSceneManagerService.$cursorPosition.set(undefined)
                    return null
                }

                const {intersection} = objectPathPartsUnderCursor[0]
                const {point} = intersection

                const vector = new Vector3(point.x, point.y, point.z)
                this.threeSceneManagerService.$cursorPosition.set(vector)

                const dropTarget = {component: this, position: vector} as TemplateDropTarget<ThreeTemplateSceneViewerComponent>

                if (
                    previous &&
                    previous.component === dropTarget.component &&
                    mathIsEqual(getVectorFromPosition(previous.position), getVectorFromPosition(dropTarget.position))
                )
                    return previous

                return dropTarget
            })
        } else return

        if (this.drag.$dropTarget() !== null) event.preventDefault()
    }

    dragLeave(event: DragEvent) {
        event.stopPropagation()

        const {nativeElement} = this.$canvas()

        this.sceneManagerService.hoverNodes(undefined)
        this.threeSceneManagerService.$cursorPosition.set(undefined)

        this.drag.dragLeave(event, this, nativeElement)
    }

    zoomIn(value: number) {
        if (this.orbitControls.enabled) {
            this.orbitControls.dollyIn(value > 1 || value < 0 ? 1.0 : 1 - value)
            this.redraw()
        } else throw new Error("Orbit controls are disabled, zooming is not possible.")
    }

    zoomOut(value: number) {
        if (this.orbitControls.enabled) {
            this.orbitControls.dollyOut(value > 1 || value < 0 ? 1.0 : 1 - value)
            this.redraw()
        } else throw new Error("Orbit controls are disabled, zooming is not possible.")
    }

    resetCamera() {
        if (this.orbitControls.enabled) {
            this.orbitControls.reset()
            this.redraw()
        } else throw new Error("Orbit controls are disabled, resetting the camera is not possible.")
    }

    setViewCamera(transform: Matrix4, target: Vector3) {
        setThreeCameraTransform(this.threeCamera, transform)
        this.orbitControls.target.set(target.x, target.y, target.z)
        this.orbitControls.update()
        this.updateViewCameraState()
        this.redraw()
    }

    private computeOrbitPivot(camera: SceneCameraParameters, transform: Matrix4, applyShift = true) {
        const {targeted, target, focalDistance, filmGauge, focalLength, shiftX, shiftY, resolutionX, resolutionY} = camera
        if (targeted) return target

        const cameraPosition = transform.getTranslation()
        const [fovX, fovY] = calculateFov(filmGauge, focalLength, resolutionX, resolutionY)
        const aimDirection = cameraAimDirection(transform, applyShift ? {shiftX, shiftY, fovX, fovY} : undefined)
        const pivotDistance = raycastCameraDistance(this.meshGeometryService, cameraPosition, aimDirection, focalDistance)

        return cameraPosition.add(aimDirection.mul(pivotDistance))
    }

    initializeCamera(camera: SceneNodes.Camera) {
        const {filmGauge, focalLength, transform} = camera

        const cameraPosition = transform.getTranslation()
        const orbitTarget = this.computeOrbitPivot(camera, transform)
        const offset = cameraPosition.sub(orbitTarget)

        const initialTransform = offset.norm() > 0 ? cameraLookAt(cameraPosition, orbitTarget) : transform.copy()

        const cameraTan = filmGauge / (2 * focalLength)
        const editorTan = this.defaultCameraSettings.filmGauge / (2 * this.defaultCameraSettings.focalLength)
        const distanceScale = cameraTan / editorTan
        if (Number.isFinite(distanceScale) && distanceScale > 0 && offset.norm() > 0) {
            initialTransform.setTranslation(orbitTarget.add(offset.mul(distanceScale)))
        }

        const {width, height} = this.$renderDimensions()
        updateThreeCamera(this.threeCamera, {...this.defaultCameraSettings, resolutionX: width, resolutionY: height}, initialTransform)

        this.orbitControls.target.set(orbitTarget.x, orbitTarget.y, orbitTarget.z)
        this.orbitControls.update()
        this.updateViewCameraState()
        this.redraw()
    }

    getBackgroundSettings() {
        const sceneOptions = this.sceneManagerService.$sceneOptions()
        const backgroundColor = sceneOptions?.backgroundColor
        const backgroundImage = sceneOptions?.backgroundImage?.downloadUrl

        const camera = this.$camera()
        const zoom = (sceneOptions?.shiftAndZoomBackgroundImage ? camera?.parameters.zoomFactor : undefined) ?? 1
        const shiftX = (sceneOptions?.shiftAndZoomBackgroundImage ? camera?.parameters.shiftX : undefined) ?? 0
        const shiftY = (sceneOptions?.shiftAndZoomBackgroundImage ? camera?.parameters.shiftY : undefined) ?? 0

        return {backgroundColor, backgroundImage: backgroundImage ? {url: backgroundImage, zoom, shiftX, shiftY} : undefined}
    }

    async captureSnapshot(mimeType: "image/png" | "image/jpeg", quality?: number, transparentBackground = false, hideOutlines = false) {
        const renderer = this.threeSceneManagerService.$renderer()
        if (!renderer) throw new Error("Renderer not available")

        // A transparent background is only possible with PNG (JPEG has no alpha channel), and it requires
        // skipping the scene's background color/image compositing so the renderer's own transparency is kept.
        if (transparentBackground) mimeType = "image/png"
        const {backgroundColor, backgroundImage} = transparentBackground
            ? {backgroundColor: undefined, backgroundImage: undefined}
            : this.getBackgroundSettings()

        const preloadBackgroundImage = async () => {
            if (!backgroundImage) return undefined
            const {url: _url, ...rest} = backgroundImage
            const blob = await (await fetchOk(backgroundImage.url)).blob()
            return {image: await createImageBitmap(blob), ...rest}
        }

        // Preload the background image *before* the forced render so there is no async gap between
        // rendering and reading the canvas. With a gap, the render loop could present a newer frame (or
        // the drawing buffer could be cleared, as preserveDrawingBuffer is off) before the pixels are
        // copied, producing a snapshot that doesn't match the frame we intended to capture.
        const preloadedBackgroundImage = await preloadBackgroundImage()

        if (hideOutlines) this.outlineVisibility.value = 0

        const taaFrame = this.sceneViewRenderPass.getTaaFrame()
        let snapshot: Promise<string>
        try {
            // Force a synchronous render right before capture to ensure the canvas buffer contains the
            // rendered frame; captureSnapshot reads the canvas synchronously before its first await.
            this.render(taaFrame)

            snapshot = captureSnapshot(
                renderer.domElement,
                this.$canvas().nativeElement,
                this.$annotations().nativeElement,
                backgroundColor,
                preloadedBackgroundImage,
                mimeType,
                quality,
            )
        } finally {
            if (hideOutlines) {
                this.outlineVisibility.value = 1
                this.render(taaFrame)
            }
        }

        return snapshot
    }

    private provideNodeSceneInteractionsContext(): INodeSceneInteractionsContext {
        return {
            modifyTemplateGraph: this.sceneManagerService.modifyTemplateGraph.bind(this.sceneManagerService),
            getTransformAccessor: this.sceneManagerService.getTransformAccessor.bind(this.sceneManagerService),
            getLiveValueAccessor: this.sceneManagerService.getLiveValueAccessor.bind(this.sceneManagerService),
            getSceneNodes: this.sceneManagerService.getSceneNodes.bind(this.sceneManagerService),
            getInfoNodeParts: this.sceneManagerService.getInfoNodeParts.bind(this.sceneManagerService),
            getSceneNodePartsUnderCursor: (pointX: number, pointY: number) => this.getSceneNodePartsUnderCursor(pointX, pointY, false),
            meshGeometryQueries: this.sceneManagerService.getISceneManager().getMeshGeometryQueries(),
            canvasRect: this.getCanvasRect(),
            camera: {
                worldInverseMatrix: fromThreeMatrix(this.threeCamera.matrixWorldInverse),
                projectionMatrix: fromThreeMatrix(this.threeCamera.projectionMatrix),
            },
            transformMode: this.sceneManagerService.$transformMode(),
        }
    }
}
